import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { FrameName } from "@/game/entities/config";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlementNames";
import { getSettlementConfigByLevel } from "@/game/entities/config/settlements";
import TileEntity from "@/game/entities/TileEntity";
import QuestRepository from "@/game/QuestRepository";
import useGameTicker from "@/hooks/useGameTicker";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useTileRepository, {
	isTilerepositoryInitialized,
} from "@/hooks/useTileRepository";
import GameError, { GameErrorType } from "@/libs/GameError";
import { type Timestamp } from "@/types";
import {
	TileEntityParam,
	type SettlementItem,
	type TileCoordinates,
} from "@/types/tile";

import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { computed, watch, type ComputedRef } from "vue";

export type SettlementKey = `${number}|${number}`;

export type SettlementValue = {
	name: string;
	level: number;
	isQuestSkipEnabled: boolean;
	availableClicks: number;
	totalClicks: number;
	wasRevealed: boolean;
	wasActivated: boolean;
	questData: {
		currentIncrement?: number; // docasna hodnota bez penalt, lebo tie este v case setovania nepozname nepozname
		current?: number;
		target?: number;
	};
};

export type SettlementsStoreState = Record<SettlementKey, SettlementValue>;

type SettlementsStoreDiffAction = {
	// type: "set";
	key: `[${SettlementKey}].${keyof SettlementValue}`;
	currentValue: SettlementValue[keyof SettlementValue];
	value: SettlementValue[keyof SettlementValue];
	// reason?: string | false;
	timestamp: Timestamp;
};

type SettlementsStorePayload = {
	state: SettlementsStoreState;
	actions: Array<SettlementsStoreDiffAction>;
};

type SettlementsStoreGetters = Record<
	SettlementKey,
	ComputedRef<SettlementValue>
>;

type SettlementsStore = SettlementsStoreGetters & {
	$getPayload: () => SettlementsStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<SettlementsStoreState>) => void;
	settlementKeys: ComputedRef<Set<SettlementKey>>;
	mainLevel: ComputedRef<number>;
	minLevel: ComputedRef<number>;
	isValidSettlementByCoordinates: ({ x, y }: TileCoordinates) => boolean;
	getLowestSettlementLevel: () => number;
	getSettlementByCoordinates: (
		coordinates: TileCoordinates,
	) => SettlementValue | null;
	setSettlementByCoordinates: (
		coordinates: TileCoordinates,
		value: SettlementValue,
	) => void;
	setSettlementPropsByCoordinates: (
		coordinates: TileCoordinates,
		value: Partial<SettlementValue>,
	) => void;
	getLevelByCoordinates: (
		coordinates: TileCoordinates,
	) => ComputedRef<number>;
	getTotalAvailableRoadCaps: ComputedRef<{
		[FrameName.GRAVEL_ROAD]: number;
		[FrameName.COBBLED_ROAD]: number;
	}>;
	// getSideSettlementsWithIncomeQuest: () => Set<SettlementKey>;
};

const STORE_NAME = "settlements";

export default defineStore(STORE_NAME, (): SettlementsStore => {
	// predpis kluca
	const _createSettlementKey = ({ x, y }: TileCoordinates): SettlementKey =>
		`${x}|${y}` as SettlementKey;

	// vsetky zname kluce
	const _settlementKeys = new Set<SettlementKey>();
	settlementNames.forEach(({ x, y }: SettlementItem): void => {
		const key = _createSettlementKey({ x, y });
		_settlementKeys.add(key);
	});

	// funkcia na dynamicke vytvorenie kluca aj s kontrolou validnosti settlementu
	const createSettlementKey = ({ x, y }: TileCoordinates): SettlementKey => {
		const key = _createSettlementKey({ x, y });

		if (!_settlementKeys.has(key))
			throw new GameError(
				GameErrorType.SETTLEMENT_ERROR,
				`Settlement store key not found for [${x}, ${y}]!`,
			);

		return key;
	};

	const state = createImmutableState<SettlementsStoreState>(
		Array.from(settlementNames.values()).reduce(
			(
				acc: SettlementsStoreState,
				{ name, x, y }: SettlementItem,
			): SettlementsStoreState => ({
				...acc,
				[createSettlementKey({ x, y })]: {
					name,
					level: 1,
					isQuestSkipEnabled: false,
					availableClicks: getSettlementConfigByLevel(1).storage.get(
						ResourceType.SETTLEMENT_CLICKS,
					)!,
					totalClicks: 0,
					wasRevealed: x === 512 && y === 512,
					wasActivated: x === 512 && y === 512,
					questData: {},
				},
			}),
			{} as SettlementsStoreState,
		),
	);

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): SettlementsStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<SettlementKey>;
		return keys.reduce(
			(
				acc: SettlementsStoreState,
				key: SettlementKey,
			): SettlementsStoreState => {
				// wasRevealed je prva vec, co sa settlementu setuje, preto to uz po tejto akcii musime ukladat
				if (!data[key].wasRevealed) return acc;
				return {
					...acc,
					[key]: data[key],
				};
			},
			{} as SettlementsStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<SettlementsStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<SettlementsStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched: boolean = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: SettlementsStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): SettlementsStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<SettlementsStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const getters = Array.from(_settlementKeys).reduce(
		(
			acc: SettlementsStoreGetters,
			key: SettlementKey,
		): SettlementsStoreGetters => ({
			...acc,
			[key]: getImmutableStateProperty(state, key, null!), // fallback nikdy nenastane, lebo sa kontroluje kluc
		}),
		{} as SettlementsStoreGetters,
	);

	const settlementKeys = computed((): Set<SettlementKey> => _settlementKeys);

	const mainLevel = computed(
		(): number =>
			state.value?.[createSettlementKey({ x: 512, y: 512 })]?.level ?? 1,
	);

	const minLevel = computed<number>((): number => {
		if (!isTilerepositoryInitialized())
			throw new GameError(
				GameErrorType.FE_ERROR,
				"TileRepository is not initialized yet!",
			);

		const tilerepo = useTileRepository();
		let min: number = Infinity;

		for (const key of _settlementKeys) {
			const settlement = state.value?.[key];
			if (!settlement) continue;
			if (!settlement.wasActivated) continue;

			const [x, y] = key.split("|").map((v: string): number => +v);
			const tileIndex = tilerepo.getTileIndexByTileCoordinates({ x, y });
			const isActive =
				tilerepo.getParam(
					tileIndex,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				) === 1;
			if (!isActive) continue;

			const level = settlement.level ?? 1;
			if (level < min) min = level;
		}

		return min === Infinity ? 1 : min;
	});

	const isValidSettlementByCoordinates = ({
		x,
		y,
	}: TileCoordinates): boolean => {
		try {
			const key = _createSettlementKey({ x, y });
			return _settlementKeys.has(key);
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error: unknown) {
			return false;
		}
	};

	const getLowestSettlementLevel = (): number => {
		const castleEntities = Array.from(settlementNames).reduce(
			(
				acc: Array<SettlementCastleBuildingTileEntity>,
				[, { x, y }]: [number, SettlementItem],
			): Array<SettlementCastleBuildingTileEntity> => {
				const index = useTileRepository().getTileIndexByTileCoordinates(
					{
						x,
						y,
					},
				);
				const entity = useTileRepository().getTileEntityByTileIndex(
					index,
				) as SettlementCastleBuildingTileEntity;
				if (entity?.isRevealed && entity?.isConnectedToMainSettlement)
					acc.push(entity);
				return acc;
			},
			new Array<SettlementCastleBuildingTileEntity>(),
		);

		const lowestSettlement = castleEntities.reduce(
			(
				a: SettlementCastleBuildingTileEntity,
				b: SettlementCastleBuildingTileEntity,
			): SettlementCastleBuildingTileEntity => {
				return a.level < b.level ? a : b;
			},
		);
		return lowestSettlement.level;
	};

	const getSettlementByCoordinates = (
		coordinates: TileCoordinates,
	): SettlementValue | null => {
		const key = createSettlementKey(coordinates);
		return state.value?.[key] ?? null;
	};

	const setSettlementByCoordinates = (
		coordinates: TileCoordinates,
		value: SettlementValue,
	): void => {
		const key = createSettlementKey(coordinates);
		setImmutableStateProperty(state, key, value);
	};

	const setSettlementPropsByCoordinates = (
		coordinates: TileCoordinates,
		value: Partial<SettlementValue>,
	): void => {
		const key = createSettlementKey(coordinates);
		setImmutableStateProperty(state, key, {
			...state.value?.[key],
			...value,
		});
	};

	const getTotalAvailableRoadCaps = computed(() => {
		const result = {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		};

		for (const key of _settlementKeys) {
			const settlement = state.value[key];
			const config = getSettlementConfigByLevel(settlement.level);
			const roadCaps = config.roadCaps as {
				[FrameName.GRAVEL_ROAD]: number;
				[FrameName.COBBLED_ROAD]: number;
			};

			result[FrameName.GRAVEL_ROAD] += roadCaps[FrameName.GRAVEL_ROAD];
			result[FrameName.COBBLED_ROAD] += roadCaps[FrameName.COBBLED_ROAD];
		}

		return result;
	});

	const getLevelByCoordinates = ({
		x,
		y,
	}: TileCoordinates): ComputedRef<number> =>
		computed(
			() => state.value?.[createSettlementKey({ x, y })]?.level ?? 1,
		);

	/*
	// TODO: mozno ako computed
	const getSideSettlementsWithIncomeQuest = (): Set<SettlementKey> => {
		const settlements = new Set<SettlementKey>();
		const levels = new Set<number>();

		for (let level = 1; level <= MAX_SETTLEMENT_LEVEL; level++) {
			const config = getSettlementConfigByLevel(level);
			if (!config) continue;

			const upgrade = config.upgrades.get(FrameName.SETTLEMENT_CASTLE) as
				| UpgradeParams
				| undefined;
			if (!upgrade) continue;

			const objective =
				(upgrade as UpgradeParams).objective?.[
					UpgradeObjectiveType.SIDE
				] ??
				(upgrade as UpgradeParams).objective?.[
					UpgradeObjectiveType.MAIN
				] ??
				null;
			if (!objective) continue;
			if (objective.action !== UpgradeObjectiveAction.SETTLEMENT_INCOME)
				continue;

			levels.add(level);
		}

		const mainSettlementKey = createSettlementKey({ x: 512, y: 512 });
		for (const key of _settlementKeys) {
			const settlement = state.value[key];
			if (key === mainSettlementKey) continue;
			if (!levels.has(settlement.level)) continue;

			settlements.add(key);
		}

		return settlements;
	};
	*/

	Array.from(_settlementKeys).forEach((key: SettlementKey): void => {
		(Object.keys(state.value[key]) as Array<keyof SettlementValue>).forEach(
			(prop: keyof SettlementValue): void => {
				watch(
					(): SettlementValue[keyof SettlementValue] =>
						state.value[key][prop],
					(
						value: SettlementValue[keyof SettlementValue],
						currentValue: SettlementValue[keyof SettlementValue],
					): void => {
						if (prop !== "level") return;

						$actions.push({
							key: `[${key}].${prop}`,
							currentValue,
							value,
							timestamp: Date.now(),
						});

						// tilerepo este nie je initnute
						if (!TileEntity.isMounted) return;

						const questRepoInstance = QuestRepository.getInstance();

						questRepoInstance.manageQuestState(
							QuestObjectiveActions.HAVE_SPECIFIC_SETTLEMENT_ON_LEVEL,
						);
						questRepoInstance.manageQuestState(
							QuestObjectiveActions.HAVE_SETTLEMENTS_ON_LEVEL,
						);
					},
				);
			},
		);
	});

	watch(
		(): number => mainLevel.value,
		(newValue: number, oldValue: number): void => {
			if (oldValue === 1 || newValue < oldValue) return;
			useGameTicker().addHandler("1:lvlPackUpdate", (): void => {
				useSocketService().emitLoadRequest({
					stores: [EmitStores.PACKS],
				});

				useGameTicker().removeHandler("1:lvlPackUpdate");
			});
		},
	);

	return {
		$getPayload,
		$resetPayload,
		$setState,
		...getters,
		settlementKeys,
		mainLevel,
		minLevel,
		isValidSettlementByCoordinates,
		getLowestSettlementLevel,
		getSettlementByCoordinates,
		setSettlementByCoordinates,
		setSettlementPropsByCoordinates,
		getLevelByCoordinates,
		// getSideSettlementsWithIncomeQuest,
		getTotalAvailableRoadCaps,
	};
});
